import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {NatResidentConstructionCompleteLocationState} from './NatResidentApplicationInterface'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    NatPutConstructionCompleteRequest,
    NatPutConstructionCompleteResponse,
} from '../repository/NatPutConstructionComplete'
import NatResidentConstructionCompleteForm from './resident/NatResidentConstructionCompleteForm'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentConstructionCompleteLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    contractorName: Nullable<string>
    constructionCompleteDate: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentConstructionCompleteInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            contractorName: null,
            constructionCompleteDate: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {applyId, condominiumCd, applicationRepo, eventEmitter} = this.props
        const applyInfo = this.props.location.state?.applyInfo

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultContractorName = applyInfo?.contractorName ?? null
        let defaultConstructionCompleteDate = applyInfo?.constructionCompleteDate ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const constructionCompleteDetail = await this.props.asyncCallFunction(
                    applicationRepo.getConstructionCompleteForResident.bind(
                        applicationRepo, condominiumCd, applyId,
                    ),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (constructionCompleteDetail && constructionCompleteDetail.details) {
                    defaultContractorName = constructionCompleteDetail.details.contractorName
                    defaultConstructionCompleteDate = constructionCompleteDetail.details.constructionCompleteDate
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            contractorName: defaultContractorName,
            constructionCompleteDate: defaultConstructionCompleteDate,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutConstructionCompleteRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutConstructionCompleteResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyConstructionComplete.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentConstructionCompleteLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        contractorName: request.contractorName,
                        constructionCompleteDate: request.constructionCompleteDate,
                    },
                    applyId: this.props.applyId,
                }

                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/constructionComplete/confirm`,
                    {state:JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/constructionComplete/${this.props.applyId}`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    工事完了届
                </PageTitle>
                <Box height={'20px'}/>
                <Box>
                    専有部分の工事が完了した場合に提出が必要です。
                </Box>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentConstructionCompleteForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentConstructionCompleteInputPage)
